import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Lock, KeyRound } from "lucide-react";
import { toast } from "sonner";

/**
 * Modal that's shown automatically the first time a freshly-onboarded
 * tenant admin logs in. Cannot be dismissed until a new password is set.
 */
export default function ForcePasswordChangeGate() {
  const { user, changePassword } = useAuth();
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (!user || user === false || !user.must_change_password) return null;

  async function submit(e) {
    e.preventDefault();
    setError("");
    if (newPw.length < 6) { setError("Neues Passwort muss mind. 6 Zeichen haben"); return; }
    if (newPw !== confirm) { setError("Passwort-Bestätigung stimmt nicht überein"); return; }
    setBusy(true);
    try {
      await changePassword(oldPw, newPw);
      toast.success("Passwort geändert. Willkommen!");
    } catch (e) {
      setError(e.response?.data?.detail || "Passwort konnte nicht geändert werden");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" data-testid="force-pw-modal">
      <form onSubmit={submit} className="w-full max-w-md bg-zinc-900 border border-zinc-800 rounded-lg p-7 shadow-2xl">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded bg-amber-500/15 border border-amber-500/30 flex items-center justify-center">
            <KeyRound className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-zinc-100">Passwort ändern</h2>
            <p className="text-xs text-zinc-500">Bitte legen Sie ein eigenes Passwort fest</p>
          </div>
        </div>

        <p className="text-sm text-zinc-400 mb-5 leading-relaxed">
          Aus Sicherheitsgründen müssen Sie das Initial-Passwort, das Ihnen Ihr Administrator
          per E-Mail zugesandt hat, jetzt durch ein eigenes Passwort ersetzen.
        </p>

        <label className="block text-xs uppercase tracking-wider text-zinc-400 mb-1.5">Aktuelles (Initial-) Passwort</label>
        <input type="password" value={oldPw} onChange={(e) => setOldPw(e.target.value)} required
               data-testid="pw-old"
               className="w-full bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2.5 text-sm text-zinc-100 mb-4 focus:outline-none focus:border-blue-500" />

        <label className="block text-xs uppercase tracking-wider text-zinc-400 mb-1.5">Neues Passwort (mind. 6 Zeichen)</label>
        <input type="password" value={newPw} onChange={(e) => setNewPw(e.target.value)} required minLength={6}
               data-testid="pw-new"
               className="w-full bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2.5 text-sm text-zinc-100 mb-4 focus:outline-none focus:border-blue-500" />

        <label className="block text-xs uppercase tracking-wider text-zinc-400 mb-1.5">Neues Passwort wiederholen</label>
        <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={6}
               data-testid="pw-confirm"
               className="w-full bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2.5 text-sm text-zinc-100 mb-5 focus:outline-none focus:border-blue-500" />

        {error && (
          <div className="mb-4 px-3 py-2 bg-red-500/10 border border-red-500/20 rounded text-xs text-red-400" data-testid="pw-error">
            {error}
          </div>
        )}

        <button type="submit" disabled={busy} data-testid="pw-submit"
                className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-medium py-2.5 rounded-md text-sm flex items-center justify-center gap-2">
          <Lock className="w-4 h-4" />
          {busy ? "Speichere..." : "Passwort speichern"}
        </button>
      </form>
    </div>
  );
}
